import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Chip,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  Business as BusinessIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useTenant } from '../contexts/TenantContext';
import { useToast } from '../hooks/useToast';

interface TenantSwitcherProps {
  variant?: 'button' | 'compact';
  showTenantName?: boolean;
}

export const TenantSwitcher: React.FC<TenantSwitcherProps> = ({
  variant = 'button',
  showTenantName = true,
}) => {
  const {
    currentTenant,
    availableTenants,
    currentFacilityGroup,
    availableFacilityGroups,
    isLoading,
    error,
    switchTenant,
    switchFacilityGroup,
    refreshTenants,
    refreshFacilityGroups,
    isMultiTenant,
  } = useTenant();
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [switching, setSwitching] = useState(false);
  const [expandedTenants, setExpandedTenants] = useState<Set<string>>(new Set());
  const { showSuccess, showError } = useToast();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isMultiTenant) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTenantSwitch = async (tenantId: string) => {
    if (tenantId === currentTenant?.id) {
      handleClose();
      return;
    }

    try {
      setSwitching(true);
      await switchTenant(tenantId);
      const newTenant = availableTenants.find(t => t.id === tenantId);
      showSuccess(`Switched to ${newTenant?.name || 'tenant'}`);
      handleClose();
      
      // Reload page to refresh tenant-specific data
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch tenant:', err);
      showError('Failed to switch tenant. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  const handleFacilityGroupSwitch = async (facilityGroupId: string) => {
    if (facilityGroupId === currentFacilityGroup?.id) {
      handleClose();
      return;
    }

    try {
      setSwitching(true);
      await switchFacilityGroup(facilityGroupId);
      const newFacilityGroup = availableFacilityGroups.find(fg => fg.id === facilityGroupId);
      showSuccess(`Switched to ${newFacilityGroup?.name || 'facility group'}`);
      handleClose();
      
      // Reload page to refresh facility group-specific data
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch facility group:', err);
      showError('Failed to switch facility group. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  const handleTenantExpand = (tenantId: string) => {
    const newExpanded = new Set(expandedTenants);
    if (newExpanded.has(tenantId)) {
      newExpanded.delete(tenantId);
    } else {
      newExpanded.add(tenantId);
    }
    setExpandedTenants(newExpanded);
  };

  const handleRefresh = async () => {
    try {
      await Promise.all([refreshTenants(), refreshFacilityGroups()]);
      showSuccess('Organizations refreshed');
    } catch (err) {
      showError('Failed to refresh organizations');
    }
  };

  const getTenantFacilityGroups = (tenantId: string) => {
    // Since facility groups ARE the tenants now, return top-level groups as "facility groups"
    return availableFacilityGroups.filter(fg => fg.parent_id === tenantId);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ maxWidth: 300 }}>
        {error}
      </Alert>
    );
  }

  if (!currentTenant) {
    return null;
  }

  const getTenantAvatar = (tenant: typeof currentTenant) => {
    if (tenant?.settings?.logo) {
      return <Avatar src={tenant.settings.logo} sx={{ width: 24, height: 24 }} />;
    }
    return (
      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
        {tenant?.name.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getTenantAvatar(currentTenant)}
        {showTenantName && (
          <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 160 }}>
            <Typography variant="body2" noWrap>
              {currentTenant.name}
            </Typography>
            {currentFacilityGroup && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {currentFacilityGroup.name}
              </Typography>
            )}
          </Box>
        )}
        {!isMultiTenant && (
          <Chip 
            label="Single Tenant" 
            size="small" 
            variant="outlined" 
            sx={{ fontSize: '0.6rem', height: 20 }}
          />
        )}
        {currentFacilityGroup && (
          <Chip 
            label="FG" 
            size="small" 
            variant="filled" 
            color="primary"
            sx={{ fontSize: '0.6rem', height: 20, minWidth: 'auto' }}
          />
        )}
      </Box>
    );
  }

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={!isMultiTenant || switching}
        startIcon={getTenantAvatar(currentTenant)}
        endIcon={isMultiTenant ? <ArrowDownIcon /> : null}
        sx={{
          textTransform: 'none',
          color: 'text.primary',
          justifyContent: 'flex-start',
          minWidth: 200,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            bgcolor: isMultiTenant ? 'action.hover' : 'background.paper',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {currentTenant.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {currentFacilityGroup ? currentFacilityGroup.name : (isMultiTenant ? 'Click to switch' : 'Single tenant mode')}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { minWidth: 300, maxWidth: 400, maxHeight: 500 }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight="medium">
            SWITCH ORGANIZATION
          </Typography>
        </Box>
        <Divider />
        
        {availableTenants.map((tenant) => {
          const tenantFacilityGroups = getTenantFacilityGroups(tenant.id);
          const isExpanded = expandedTenants.has(tenant.id);
          const hasFacilityGroups = tenantFacilityGroups.length > 0;
          
          return (
            <Box key={tenant.id}>
              <MenuItem
                onClick={() => {
                  if (hasFacilityGroups) {
                    handleTenantExpand(tenant.id);
                  } else {
                    handleTenantSwitch(tenant.id);
                  }
                }}
                disabled={switching}
                selected={tenant.id === currentTenant?.id && !currentFacilityGroup}
                sx={{ pr: hasFacilityGroups ? 1 : 2 }}
              >
                <ListItemIcon>
                  {tenant.id === currentTenant?.id && !currentFacilityGroup ? (
                    <CheckIcon color="primary" />
                  ) : (
                    getTenantAvatar(tenant)
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={tenant.name}
                  secondary={`${tenantFacilityGroups.length} facility groups`}
                  primaryTypographyProps={{ noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                />
                {hasFacilityGroups && (
                  <Box sx={{ ml: 1 }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                )}
              </MenuItem>
              
              {hasFacilityGroups && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {tenantFacilityGroups.map((facilityGroup) => (
                      <ListItem key={facilityGroup.id} disablePadding>
                        <ListItemButton
                          onClick={() => handleFacilityGroupSwitch(facilityGroup.id)}
                          disabled={switching}
                          selected={facilityGroup.id === currentFacilityGroup?.id}
                          sx={{ pl: 6, pr: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {facilityGroup.id === currentFacilityGroup?.id ? (
                            <CheckIcon color="primary" fontSize="small" />
                          ) : (
                            <LocationIcon fontSize="small" color="action" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={facilityGroup.name}
                          secondary={facilityGroup.description || facilityGroup.code}
                          primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
                          secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
                        />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
        
        <Divider />
        <MenuItem onClick={handleRefresh} disabled={switching}>
          <ListItemIcon>
            <RefreshIcon />
          </ListItemIcon>
          <ListItemText primary="Refresh Organizations" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default TenantSwitcher;